import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import React, { useState, useMemo, useEffect, useRef } from 'react';
import { Header } from '../components/layout/Header';
import { BottomNav } from '../components/layout/BottomNav';
import { PageLayout } from '../components/layout/PageLayout';
import { LoadingLogo } from '../components/common/LoadingLogo';
import { useUserStore } from '../store/useUserStore';
import { useAuth } from '../hooks/useAuth';
import {
  useGetProject,
  useUpdateProject,
  useDeleteProject,
  mapApiProjectToProject,
  PROJECT_TYPE_LABEL_TO_API,
  PROJECT_STATUS_LABEL_TO_API,
} from '../api/projects';
import { ProjectCollaboratorsCard } from '../components/projects/ProjectCollaboratorsCard';
import { CoverCropModal } from '../components/common/CoverCropModal';
import { createCroppedImageFile, convertHeicToJpeg, type PixelCropArea } from '../utils/imageCrop';
import { uploadFilesToCloudinary } from '../utils/cloudinary';
import { toast } from '../utils/toast';

import {
  FiArrowLeft,
  FiCalendar,
  FiMap,
  FiTv,
  FiMusic,
  FiFlag,
  FiBox,
  FiX,
  FiImage,
  FiMaximize2,
  FiMinimize2,
  FiUploadCloud,
  FiEye,
  FiCrop,
} from 'react-icons/fi';
import { AnimatePresence, motion } from 'framer-motion';

export const Route = createFileRoute('/projects/$projectId/edit')({
  component: EditProjectPage,
});

function EditProjectPage() {
  const { projectId } = Route.useParams();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();

  useEffect(() => {
    if (!isAuthenticated) {
      navigate({ to: '/sign-in', search: { redirect: `/projects/${projectId}/edit` } });
    }
  }, [isAuthenticated, navigate, projectId]);

  const { user } = useUserStore();
  const { data: apiProject, isLoading } = useGetProject(projectId);
  const { mutate: updateProjectMutation } = useUpdateProject();
  const { mutate: deleteProjectMutation, isPending: isDeleting } = useDeleteProject();

  const project = useMemo(
    () => (apiProject ? mapApiProjectToProject(apiProject, user?.id) : undefined),
    [apiProject, user?.id],
  );

  const canArchive = project?.permissions ? project.permissions.can_archive : false;

  // Form states
  const [name, setName] = useState(project?.title || '');
  const [description, setDescription] = useState(project?.description || '');
  const [projectType, setProjectType] = useState<string>(project?.projectTypes?.[0] || 'Event');
  const [customProjectType, setCustomProjectType] = useState(project?.customProjectType || '');
  const [status, setStatus] = useState(project?.status || 'Planning');
  const [startDate, setStartDate] = useState(project?.rawStartDate || '');
  const [endDate, setEndDate] = useState(project?.rawEndDate || '');
  const [showBudget, setShowBudget] = useState(true);
  const [nameError, setNameError] = useState<string | undefined>();
  const [customProjectTypeError, setCustomProjectTypeError] = useState<string | undefined>();
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [imageError, setImageError] = useState<string | undefined>();
  const [objectFitMode, setObjectFitMode] = useState<'cover' | 'contain'>('cover');
  const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false);
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  const imageInputRef = useRef<HTMLInputElement>(null);

  // Cover crop states
  const [isCropModalOpen, setIsCropModalOpen] = useState(false);
  const [cropSourceFile, setCropSourceFile] = useState<File | null>(null);
  const [cropSourceUrl, setCropSourceUrl] = useState<string | null>(null);
  const [isApplyingCrop, setIsApplyingCrop] = useState(false);

  useEffect(() => {
    return () => {
      if (imagePreview) URL.revokeObjectURL(imagePreview);
      if (cropSourceUrl) URL.revokeObjectURL(cropSourceUrl);
    };
  }, [imagePreview, cropSourceUrl]);

  const handleImageChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setImageError('Please select a valid image file.');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setImageError('Image file size must be 5 MB or smaller.');
      return;
    }
    setImageError(undefined);

    try {
      const convertedFile = await convertHeicToJpeg(file);
      if (cropSourceUrl) URL.revokeObjectURL(cropSourceUrl);
      const url = URL.createObjectURL(convertedFile);
      setCropSourceFile(convertedFile);
      setCropSourceUrl(url);
      setIsCropModalOpen(true);
    } catch (err) {
      console.error('Failed to prepare image for crop:', err);
      setImageError('Failed to process image. Please try another file.');
    }
  };

  const handleOpenRecrop = () => {
    if (cropSourceUrl) {
      setIsCropModalOpen(true);
    } else if (imageInputRef.current) {
      imageInputRef.current.value = '';
      imageInputRef.current.click();
    }
  };

  const applyCoverCrop = async (cropAreaPixels: PixelCropArea) => {
    if (!cropSourceFile || !cropSourceUrl) return;
    setIsApplyingCrop(true);
    try {
      const croppedFile = await createCroppedImageFile({
        imageSrc: cropSourceUrl,
        cropAreaPixels,
        fileName: cropSourceFile.name,
      });

      if (imagePreview) URL.revokeObjectURL(imagePreview);
      const nextPreviewUrl = URL.createObjectURL(croppedFile);
      setImageFile(croppedFile);
      setImagePreview(nextPreviewUrl);
      setIsCropModalOpen(false);
    } catch (err) {
      console.error('Failed to apply cover crop:', err);
      setImageError('Failed to crop image. Please try again.');
    } finally {
      setIsApplyingCrop(false);
    }
  };

  const handleRemovePendingImage = () => {
    setImageFile(null);
    setImagePreview(null);
    if (cropSourceUrl) {
      URL.revokeObjectURL(cropSourceUrl);
      setCropSourceUrl(null);
    }
    setCropSourceFile(null);
    setImageError(undefined);
    if (imageInputRef.current) imageInputRef.current.value = '';
  };

  // Form state is initialized before the project has loaded, so sync it once
  // the real project data arrives (or when navigating to a different project).
  useEffect(() => {
    if (!project) return;
    setName(project.title);
    setDescription(project.description);
    // A project with a filled-in custom_project_type is treated as Custom even
    // if project_type wasn't literally 'custom' on the record.
    const isCustom = project.projectTypes[0] === 'Custom' || !!project.customProjectType;
    setProjectType(isCustom ? 'Custom' : project.projectTypes[0] || 'Event');
    setCustomProjectType(project.customProjectType || '');
    setStatus(project.status);
    setStartDate(project.rawStartDate || '');
    setEndDate(project.rawEndDate || '');
    setShowBudget(project.showBudgetToCollaborators);
  }, [project?.id]);

  // Permissions are the sole source of truth for edit access. When the field is absent
  // (older payloads without a `permissions` object) we fail open so existing behavior
  // is unaffected; once present, an explicit `can_edit: false` always wins.
  const canEdit = project?.permissions ? project.permissions.can_edit : true;
  const canDelete = project?.permissions ? project.permissions.can_delete : true;

  // Direct URL access to the edit route is blocked here — bounce back to Project
  // Details rather than rendering a form the user isn't allowed to submit.
  useEffect(() => {
    if (project && !canEdit) {
      navigate({ to: '/projects/$projectId', params: { projectId: project.id } });
    }
  }, [project, canEdit, navigate]);

  if (isLoading) {
    return <LoadingLogo />;
  }

  if (!project) {
    return (
      <div className="min-h-screen bg-black relative pb-32">
        <Header />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 text-center">
          <p className="text-zinc-400 text-sm">Project not found</p>
          <Link
            to="/projects"
            className="text-blue-500 hover:text-blue-400 text-xs font-semibold mt-4 inline-block"
          >
            Back to projects
          </Link>
        </div>
        <BottomNav />
      </div>
    );
  }

  if (!canEdit) {
    // Redirect effect above is already in flight — render nothing but a loader
    // instead of flashing editable fields the user isn't permitted to submit.
    return <LoadingLogo />;
  }

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (project.isArchived || !canEdit) return;

    const trimmedName = name.trim();
    const trimmedCustomType = customProjectType.trim();
    let hasError = false;
    if (!trimmedName) {
      setNameError('Project name is required.');
      hasError = true;
    } else {
      setNameError(undefined);
    }
    if (projectType === 'Custom' && !trimmedCustomType) {
      setCustomProjectTypeError('Custom Project Type is required.');
      hasError = true;
    } else {
      setCustomProjectTypeError(undefined);
    }
    if (hasError) return;

    let uploadedImageUrl: string | undefined;
    if (imageFile) {
      setIsUploadingImage(true);
      try {
        const results = await uploadFilesToCloudinary([imageFile], {
          folder: 'project_images',
          uploadPreset: 'service_preset',
        });
        uploadedImageUrl = results[0]?.secure_url;
      } catch (err) {
        console.error('Cloudinary upload error:', err);
        toast.error('Failed to upload image. Please try again.');
        setIsUploadingImage(false);
        return;
      }
      setIsUploadingImage(false);
    }

    updateProjectMutation(
      {
        projectUuid: project.id,
        payload: {
          name: trimmedName,
          description,
          project_type: PROJECT_TYPE_LABEL_TO_API[projectType],
          custom_project_type: projectType === 'Custom' ? trimmedCustomType : '',
          status: PROJECT_STATUS_LABEL_TO_API[status],
          start_date: startDate || null,
          end_date: endDate || null,
          show_budget_to_collaborators: showBudget,
          ...(uploadedImageUrl ? { image: uploadedImageUrl } : {}),
        },
      },
      {
        onSuccess: () => {
          setImageFile(null);
          setImagePreview(null);
          navigate({ to: '/projects/$projectId', params: { projectId: project.id } });
        },
      },
    );
  };

  const handleArchive = () => {
    if (!canArchive) {
      toast.error('Editors can only archive projects they created.');
      return;
    }
    updateProjectMutation(
      { projectUuid: project.id, payload: { status: project.isArchived ? 'active' : 'archived' } },
      {
        onSuccess: () =>
          navigate({ to: '/projects', search: { workspace: project.workspaceUuid } }),
      },
    );
  };

  const handleDelete = () => {
    if (!canDelete || project.isArchived) return;
    deleteProjectMutation(project.id, {
      onSuccess: () => navigate({ to: '/projects', search: { workspace: project.workspaceUuid } }),
    });
  };

  const typeOptions = [
    { name: 'Event', icon: <FiCalendar className="w-3.5 h-3.5" /> },
    { name: 'Tour', icon: <FiMap className="w-3.5 h-3.5" /> },
    { name: 'Music Video', icon: <FiTv className="w-3.5 h-3.5" /> },
    { name: 'Album Rollout', icon: <FiMusic className="w-3.5 h-3.5" /> },
    { name: 'Festival', icon: <FiFlag className="w-3.5 h-3.5" /> },
    { name: 'Custom', icon: <FiBox className="w-3.5 h-3.5" /> },
  ] as const;

  const statusOptions = [
    { name: 'Planning', dotColor: 'bg-amber-500' },
    { name: 'Active', dotColor: 'bg-emerald-500' },
    { name: 'Completed', dotColor: 'bg-blue-500' },
  ] as const;

  return (
    <div className="min-h-screen bg-black relative pb-32">
      <Header />

      <PageLayout>
        <form onSubmit={handleSave} className="max-w-3xl mx-auto py-6 space-y-6">
          {/* Settings Sub-Header */}
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between pb-4 sm:pb-2 border-b border-white/5">
            <div className="flex items-center gap-2 text-xs font-semibold text-zinc-450">
              <Link
                to="/projects/$projectId"
                params={{ projectId: project.id }}
                className="flex items-center gap-2 hover:text-white transition-colors cursor-pointer"
              >
                <FiArrowLeft className="w-4 h-4" />
                <span>Back to Project</span>
              </Link>
              <span className="text-zinc-650">/</span>
              <span className="text-zinc-550">Edit Project</span>
            </div>

            <div className="flex items-center gap-3 w-full sm:w-auto justify-end">
              {canArchive && (
                <button
                  type="button"
                  onClick={handleArchive}
                  className="flex-1 sm:flex-none px-5 py-2 rounded-full border border-rose-500/20 bg-rose-500/10 hover:bg-rose-500/20 text-rose-450 hover:text-rose-350 text-xs font-bold transition-all cursor-pointer text-center"
                >
                  {project.isArchived ? 'Unarchive' : 'Archive'}
                </button>
              )}
              <button
                type="submit"
                disabled={project.isArchived || isUploadingImage}
                className="flex-1 sm:flex-none px-6 py-2 rounded-full bg-white text-black hover:bg-zinc-100 text-xs font-bold transition-all cursor-pointer shadow-lg shadow-white/5 active:scale-95 text-center disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-white"
              >
                {isUploadingImage ? 'Uploading...' : 'Save Changes'}
              </button>
            </div>
          </div>

          {project.isArchived && (
            <div className="px-4 py-3 rounded-2xl border border-amber-500/20 bg-amber-500/5 text-amber-400 text-xs font-semibold">
              This project is archived and read-only. Unarchive it to make changes.
            </div>
          )}

          <fieldset
            disabled={project.isArchived}
            className="space-y-6 border-0 p-0 m-0 min-w-0 disabled:opacity-50"
          >
            {/* CARD 1: Project Details */}
            <div className="bg-[#0B0B0B] border border-white/10 rounded-[28px] p-4 sm:p-6 space-y-5">
              <div>
                <h3 className="text-base font-bold text-white">Project Details</h3>
                <p className="text-zinc-550 text-xs mt-1">
                  Update the basic information about this project.
                </p>
              </div>

              <div className="space-y-4">
                <div>
                  <div className="flex items-center justify-between mb-2.5">
                    <label className="block text-zinc-400 text-[10px] font-bold uppercase tracking-wider">
                      Project Image
                    </label>
                    <button
                      type="button"
                      onClick={() =>
                        setObjectFitMode((prev) => (prev === 'cover' ? 'contain' : 'cover'))
                      }
                      className="flex items-center gap-1.5 text-[10px] font-semibold text-zinc-300 hover:text-white transition-colors cursor-pointer bg-white/5 hover:bg-white/10 px-3 py-1 rounded-full border border-white/10"
                      title={
                        objectFitMode === 'cover'
                          ? 'Switch to Contain (view entire image)'
                          : 'Switch to Cover (fill banner)'
                      }
                    >
                      {objectFitMode === 'cover' ? (
                        <>
                          <FiMaximize2 className="w-3 h-3 text-blue-400" />
                          <span>View Mode: Fill</span>
                        </>
                      ) : (
                        <>
                          <FiMinimize2 className="w-3 h-3 text-emerald-400" />
                          <span>View Mode: Full Uncropped</span>
                        </>
                      )}
                    </button>
                  </div>

                  <div className="relative w-full h-44 sm:h-56 md:h-64 rounded-2xl overflow-hidden border border-white/10 bg-[#060606] flex items-center justify-center group shadow-xl">
                    <img
                      src={imagePreview || project.bgImage}
                      alt="Project"
                      className={`w-full h-full transition-all duration-300 ${
                        objectFitMode === 'cover' ? 'object-cover' : 'object-contain p-4'
                      }`}
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/20 to-black/30 opacity-90 sm:opacity-80 group-hover:opacity-95 transition-opacity flex flex-col justify-between p-5">
                      <div className="flex items-center justify-between">
                        <span className="px-3.5 py-1.5 rounded-full bg-black/70 backdrop-blur-md border border-white/15 text-white text-xs font-semibold flex items-center gap-2 shadow-lg">
                          <FiImage className="w-4 h-4 text-blue-400" />
                          {imageFile ? 'New Image Preview' : 'Current Project Cover Banner'}
                        </span>
                        <button
                          type="button"
                          onClick={() => setIsPreviewModalOpen(true)}
                          className="px-3 py-1.5 rounded-full bg-black/70 hover:bg-black/90 backdrop-blur-md border border-white/15 text-white text-xs font-medium flex items-center gap-1.5 transition-all cursor-pointer shadow-lg"
                        >
                          <FiEye className="w-3.5 h-3.5 text-purple-400" />
                          <span>Expand</span>
                        </button>
                      </div>

                      <div className="flex items-center justify-between gap-3 flex-wrap">
                        <div className="flex items-center gap-2">
                          <input
                            ref={imageInputRef}
                            type="file"
                            accept="image/*"
                            className="hidden"
                            onChange={handleImageChange}
                          />
                          <button
                            type="button"
                            onClick={() => imageInputRef.current?.click()}
                            className="px-5 py-2.5 rounded-full border border-white/20 bg-white/15 hover:bg-white/25 backdrop-blur-md text-white text-xs font-bold transition-all cursor-pointer shadow-xl flex items-center gap-2 active:scale-95"
                          >
                            <FiUploadCloud className="w-4 h-4 text-blue-400" />
                            <span>{imageFile ? 'Choose Different Image' : 'Change Image'}</span>
                          </button>
                          {cropSourceUrl && (
                            <button
                              type="button"
                              onClick={handleOpenRecrop}
                              className="px-4 py-2.5 rounded-full border border-white/20 bg-white/15 hover:bg-white/25 backdrop-blur-md text-white text-xs font-bold transition-all cursor-pointer shadow-xl flex items-center gap-1.5 active:scale-95"
                            >
                              <FiCrop className="w-3.5 h-3.5 text-emerald-400" />
                              <span>Adjust Crop</span>
                            </button>
                          )}
                          {imageFile && (
                            <button
                              type="button"
                              onClick={handleRemovePendingImage}
                              title="Cancel change"
                              className="p-2.5 rounded-full border border-rose-500/30 bg-rose-500/20 hover:bg-rose-500/35 text-rose-300 transition-all cursor-pointer backdrop-blur-md active:scale-95"
                            >
                              <FiX className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center justify-between mt-2.5 px-1">
                    <p className="text-[10px] text-zinc-550">Recommended: PNG or JPG up to 5 MB.</p>
                    {imageError && (
                      <p className="text-rose-500 text-[10px] font-semibold">{imageError}</p>
                    )}
                  </div>
                </div>

                {/* Lightbox Modal */}
                <AnimatePresence>
                  {isPreviewModalOpen && (
                    <div className="fixed inset-0 z-[120] flex items-center justify-center p-4 sm:p-6 bg-black/90 backdrop-blur-md">
                      <motion.div
                        initial={{ opacity: 0, scale: 0.95 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.95 }}
                        className="relative w-full max-w-5xl max-h-[90vh] flex flex-col bg-[#0B0B0B] border border-white/15 rounded-3xl overflow-hidden shadow-2xl"
                      >
                        <div className="flex items-center justify-between px-6 py-4 border-b border-white/10 bg-[#0F0F10]">
                          <div className="flex items-center gap-2">
                            <FiImage className="w-4 h-4 text-blue-400" />
                            <h3 className="text-white text-sm font-bold">Project Image Lightbox</h3>
                          </div>
                          <button
                            type="button"
                            onClick={() => setIsPreviewModalOpen(false)}
                            className="p-2 rounded-full text-zinc-400 hover:text-white hover:bg-white/10 transition-all cursor-pointer"
                          >
                            <FiX className="w-5 h-5" />
                          </button>
                        </div>
                        <div className="flex-1 p-4 flex items-center justify-center overflow-auto bg-black/40 min-h-[300px]">
                          <img
                            src={imagePreview || project.bgImage}
                            alt="Project Lightbox"
                            className="max-w-full max-h-[75vh] object-contain rounded-xl border border-white/5 shadow-2xl"
                          />
                        </div>
                      </motion.div>
                    </div>
                  )}
                </AnimatePresence>

                <div>
                  <label className="block text-zinc-400 text-[10px] font-bold uppercase tracking-wider mb-2">
                    Project Name <span className="text-rose-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => {
                      setName(e.target.value);
                      if (nameError) setNameError(undefined);
                    }}
                    className={`w-full bg-[#0F0F0F] border hover:border-white/10 focus:border-blue-500 outline-none text-white text-xs rounded-xl px-4 py-3.5 transition-all ${
                      nameError ? 'border-rose-500' : 'border-white/5'
                    }`}
                  />
                  {nameError && (
                    <p className="text-rose-500 text-[10px] font-semibold mt-1.5">{nameError}</p>
                  )}
                </div>

                <div>
                  <label className="block text-zinc-400 text-[10px] font-bold uppercase tracking-wider mb-2">
                    Description
                  </label>
                  <textarea
                    placeholder="Describe the project..."
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    rows={4}
                    className="w-full bg-[#0F0F0F] border border-white/5 hover:border-white/10 focus:border-blue-500 outline-none text-white text-xs rounded-xl px-4 py-3.5 resize-none transition-all"
                  />
                </div>

                {/* Project Type */}
                <div>
                  <label className="block text-zinc-400 text-[10px] font-bold uppercase tracking-wider mb-2.5">
                    Project Type
                  </label>
                  <div className="flex flex-wrap gap-2.5">
                    {typeOptions.map((opt) => {
                      const isSelected = projectType === opt.name;
                      return (
                        <button
                          key={opt.name}
                          type="button"
                          onClick={() => {
                            setProjectType(opt.name);
                            if (opt.name !== 'Custom') {
                              setCustomProjectType('');
                              setCustomProjectTypeError(undefined);
                            }
                          }}
                          className={`flex items-center gap-1.5 px-4 py-2 rounded-full border text-xs font-semibold cursor-pointer transition-all ${
                            isSelected
                              ? 'bg-blue-500 text-white border-blue-500 shadow-md shadow-blue-500/10'
                              : 'border-white/5 bg-[#0F0F0F]/60 text-zinc-400 hover:text-white hover:border-white/10'
                          }`}
                        >
                          {opt.icon}
                          <span>{opt.name}</span>
                        </button>
                      );
                    })}
                  </div>
                  <AnimatePresence>
                    {projectType === 'Custom' && (
                      <motion.div
                        initial={{ opacity: 0, height: 0, marginTop: 0 }}
                        animate={{ opacity: 1, height: 'auto', marginTop: 10 }}
                        exit={{ opacity: 0, height: 0, marginTop: 0 }}
                        transition={{ duration: 0.2 }}
                        className="overflow-hidden"
                      >
                        <label className="block text-zinc-400 text-[10px] font-bold uppercase tracking-wider mb-2">
                          Custom Project Type <span className="text-rose-500">*</span>
                        </label>
                        <input
                          type="text"
                          placeholder="e.g. Marketing Campaign"
                          value={customProjectType}
                          onChange={(e) => {
                            setCustomProjectType(e.target.value);
                            if (customProjectTypeError) setCustomProjectTypeError(undefined);
                          }}
                          className={`w-full bg-[#0F0F0F] border hover:border-white/10 focus:border-blue-500 outline-none text-white text-xs rounded-xl px-4 py-3.5 transition-all ${
                            customProjectTypeError ? 'border-rose-500' : 'border-white/5'
                          }`}
                        />
                        {customProjectTypeError && (
                          <p className="text-rose-500 text-[10px] font-semibold mt-1.5">
                            {customProjectTypeError}
                          </p>
                        )}
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>

                {/* Status */}
                <div>
                  <label className="block text-zinc-400 text-[10px] font-bold uppercase tracking-wider mb-2.5">
                    Status
                  </label>
                  <div className="flex flex-wrap gap-3">
                    {statusOptions.map((opt) => {
                      const isSelected = status === opt.name;
                      return (
                        <button
                          key={opt.name}
                          type="button"
                          onClick={() => setStatus(opt.name)}
                          className={`flex items-center gap-2 px-4 py-2 rounded-full border text-xs font-semibold cursor-pointer transition-all ${
                            isSelected
                              ? 'bg-white text-black border-white shadow-md'
                              : 'border-white/5 bg-[#0F0F0F]/60 text-zinc-400 hover:text-white hover:border-white/10'
                          }`}
                        >
                          <span className={`w-1.5 h-1.5 rounded-full ${opt.dotColor}`} />
                          <span>{opt.name}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Dates */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-zinc-400 text-[10px] font-bold uppercase tracking-wider mb-2">
                      Start Date
                    </label>
                    <div className="relative">
                      <FiCalendar className="absolute left-4 top-1/2 -translate-y-1/2 text-zinc-550 w-4 h-4" />
                      <input
                        type="date"
                        value={startDate}
                        onChange={(e) => setStartDate(e.target.value)}
                        className="w-full bg-[#0F0F0F] border border-white/5 outline-none text-white text-xs rounded-xl pl-11 pr-4 py-3.5 focus:border-blue-500 transition-all [color-scheme:dark]"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-zinc-400 text-[10px] font-bold uppercase tracking-wider mb-2">
                      End Date
                    </label>
                    <div className="relative">
                      <FiCalendar className="absolute left-4 top-1/2 -translate-y-1/2 text-zinc-550 w-4 h-4" />
                      <input
                        type="date"
                        value={endDate}
                        onChange={(e) => setEndDate(e.target.value)}
                        className="w-full bg-[#0F0F0F] border border-white/5 outline-none text-white text-xs rounded-xl pl-11 pr-4 py-3.5 focus:border-blue-500 transition-all [color-scheme:dark]"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* CARD 2: Visibility & Permissions */}
            <div className="bg-[#0B0B0B] border border-white/10 rounded-[28px] p-4 sm:p-6 space-y-4">
              <div>
                <h3 className="text-base font-bold text-white">Visibility & Permissions</h3>
                <p className="text-zinc-550 text-xs mt-1">
                  Control what collaborators can see in this project.
                </p>
              </div>

              <div className="flex items-center justify-between gap-4 p-4 bg-[#0F0F0F]/65 border border-white/5 rounded-2xl">
                <div className="min-w-0">
                  <p className="text-white text-xs font-bold">Show Budget to Collaborators</p>
                  <p className="text-[10px] text-zinc-550 mt-1 leading-normal">
                    When off, budgeting tabs are hidden from all crew members except the project
                    owner.
                  </p>
                </div>

                {/* Toggle switch */}
                <button
                  type="button"
                  onClick={() => setShowBudget(!showBudget)}
                  className={`w-11 h-6 rounded-full p-0.5 transition-colors cursor-pointer shrink-0 ${
                    showBudget ? 'bg-blue-500' : 'bg-zinc-800'
                  }`}
                >
                  <div
                    className={`w-5 h-5 rounded-full bg-white transition-transform ${
                      showBudget ? 'translate-x-5' : 'translate-x-0'
                    }`}
                  />
                </button>
              </div>
            </div>

            {/* CARD 3: Collaborators & Access */}
            <ProjectCollaboratorsCard
              projectUuid={project.id}
              currentUserId={user?.id}
              isOwner={project.isMyProject}
              permissions={project.permissions}
            />
          </fieldset>

          {(canArchive || canDelete) && (
            /* CARD 4: Danger Zone */
            <div className="bg-[#0B0B0B] border border-rose-500/10 rounded-[28px] p-4 sm:p-6 space-y-4 mt-6">
              <div>
                <h3 className="text-base font-bold text-rose-500">Danger Zone</h3>
                <p className="text-zinc-550 text-xs mt-1">Irreversible actions for this project.</p>
              </div>

              {canArchive && (
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 p-4 bg-rose-500/[0.02] border border-rose-500/10 rounded-2xl">
                  <div className="min-w-0">
                    <p className="text-white text-xs font-bold">Archive Project</p>
                    <p className="text-[10px] text-zinc-550 mt-1 leading-normal">
                      Hide this project from the workspace. It will still be available in the
                      Archived list.
                    </p>
                  </div>

                  <button
                    type="button"
                    onClick={handleArchive}
                    className="px-5 py-2 rounded-full border border-rose-500/20 bg-rose-500/10 hover:bg-rose-500/20 text-rose-450 hover:text-rose-350 text-xs font-bold transition-all cursor-pointer shrink-0 w-full sm:w-auto text-center"
                  >
                    {project.isArchived ? 'Unarchive' : 'Archive'}
                  </button>
                </div>
              )}

              {canDelete && (
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 p-4 bg-rose-500/[0.02] border border-rose-500/10 rounded-2xl">
                  <div className="min-w-0">
                    <p className="text-white text-xs font-bold">Delete Project</p>
                    <p className="text-[10px] text-zinc-550 mt-1 leading-normal">
                      Permanently delete this project and all of its data. This cannot be undone.
                    </p>
                  </div>

                  <button
                    type="button"
                    onClick={() => setIsDeleteModalOpen(true)}
                    disabled={project.isArchived}
                    className="px-5 py-2 rounded-full border border-rose-500/30 bg-rose-500 hover:bg-rose-600 text-white text-xs font-bold transition-all cursor-pointer shrink-0 w-full sm:w-auto text-center disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-rose-500"
                  >
                    Delete Project
                  </button>
                </div>
              )}
            </div>
          )}
        </form>
      </PageLayout>

      <AnimatePresence>
        {isDeleteModalOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setIsDeleteModalOpen(false)}
              className="absolute inset-0 bg-black/80 backdrop-blur-md"
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              transition={{ type: 'spring', duration: 0.4 }}
              className="relative w-full max-w-sm bg-[#0B0B0B] border border-white/10 rounded-[28px] p-6 text-left flex flex-col z-10 shadow-[0_20px_50px_rgba(0,0,0,0.8)]"
            >
              <div className="flex items-start justify-between mb-4">
                <h2 className="text-lg font-bold text-white">Delete Project?</h2>
                <button
                  onClick={() => setIsDeleteModalOpen(false)}
                  className="p-1.5 rounded-full border border-white/10 hover:border-white/20 bg-zinc-900/60 hover:bg-zinc-800 text-zinc-400 hover:text-white transition-all cursor-pointer"
                >
                  <FiX className="w-4 h-4" />
                </button>
              </div>
              <p className="text-zinc-400 text-xs mb-6">
                This will permanently delete{' '}
                <span className="text-white font-semibold">{project.title}</span> and all of its
                data. This action cannot be undone.
              </p>
              <div className="flex items-center justify-end gap-3">
                <button
                  type="button"
                  onClick={() => setIsDeleteModalOpen(false)}
                  className="px-5 py-2.5 rounded-full border border-white/10 bg-zinc-900/60 hover:bg-zinc-800 text-zinc-300 text-xs font-bold transition-all cursor-pointer"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleDelete}
                  disabled={isDeleting}
                  className="px-5 py-2.5 rounded-full bg-rose-500 hover:bg-rose-600 text-white text-xs font-bold transition-all cursor-pointer disabled:opacity-50"
                >
                  {isDeleting ? 'Deleting...' : 'Delete Project'}
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      <CoverCropModal
        isOpen={isCropModalOpen}
        imageSrc={cropSourceUrl}
        isSubmitting={isApplyingCrop}
        aspect={16 / 9}
        objectFit="contain"
        title="Adjust project cover crop"
        description="Drag and scale to choose what appears in your project banner cover"
        onCancel={() => setIsCropModalOpen(false)}
        onConfirm={applyCoverCrop}
      />

      <BottomNav />
    </div>
  );
}
